import useReportSort from '../../components/reports/useReportSort';
import { useState } from "react";
import {
  FiDownload,
  FiFileText,
  FiFilter,
  FiLoader,
  FiRefreshCw,
  FiShoppingBag,
  FiShield,
} from "react-icons/fi";
import ReportFilterModal from "../../components/reports/ReportFilterModal";
import { useAuth } from "../../context/AuthContext";
import { apiRequest } from "../../services/api";
import { hasPermission } from "../../utils/permissions";
const money = (v) =>
  `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const date = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
function download(blob) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "charms-purchase-report.xlsx";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(a.href);
}
export default function PurchaseReportPage() {
  const { accessToken, user } = useAuth();
  const now = new Date();
  const [filters, setFilters] = useState({
    from: date(new Date(now.getFullYear(), now.getMonth(), 1)),
    to: date(now),
  });
  const [draftFilters, setDraftFilters] = useState(filters);
  const [showFilters, setShowFilters] = useState(false);
  const [rawReport, setReport] = useState(null);
  const { report, controls: sortControls } = useReportSort(rawReport);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const canGenerate = hasPermission(user, "reports.purchase.generate");
  const canExport = hasPermission(user, "reports.purchase.export");
  const generate = async (nextFilters = filters) => {
    setLoading(true);
    try {
      setReport(
        await apiRequest("/business-reports/purchase/generate", {
          method: "POST",
          body: nextFilters,
          accessToken,
        }),
      );
      setError("");
    } catch (e) {
      setError(e.message || "Could not generate purchase report.");
    } finally {
      setLoading(false);
    }
  };
  const applyFilters = async () => {
    const nextFilters = { ...draftFilters };
    setFilters(nextFilters);
    await generate(nextFilters);
    setShowFilters(false);
  };
  const exportExcel = async () => {
    setExporting(true);
    try {
      const r = await apiRequest(
        `/business-reports/purchase/export?${new URLSearchParams({ ...filters, format: "XLSX" })}`,
        { accessToken, rawResponse: true },
      );
      download(await r.blob());
    } catch (e) {
      setError(e.message || "Could not export purchase report.");
    } finally {
      setExporting(false);
    }
  };
  return (
    <div className="business-report space-y-6">
      <section>
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="flex gap-2 text-xs font-semibold uppercase tracking-[.18em]">
              <FiShoppingBag /> Business reports
            </div>
            <h1>Purchase Report</h1>
            <p>
              Posted GRN purchases with supplier invoice, GST, freight and
              landed-cost details.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => {
                setDraftFilters({ ...filters });
                setShowFilters(true);
              }}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-700"
            >
              <FiFilter /> Filters
            </button>
            <button
              type="button"
              onClick={() => generate()}
              disabled={!canGenerate || loading}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-800 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {loading ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiRefreshCw />
              )}
              {loading ? "Generating" : "Generate"}
            </button>
            <button
              type="button"
              onClick={exportExcel}
              disabled={!report || !canExport || exporting}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 disabled:opacity-50"
            >
              {exporting ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiDownload />
              )}
              {exporting ? "Preparing" : "Export Excel"}
            </button>
          </div>
        </div>
      </section>
      {!canGenerate && (
        <p className="mx-4 flex gap-2 rounded-lg bg-amber-50 p-4 text-sm text-amber-800">
          <FiShield /> You do not have permission to generate this report.
        </p>
      )}
      {error && (
        <p className="rounded-xl bg-red-50 p-4 text-sm text-red-700">{error}</p>
      )}
      {report && (
        <>
          <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
            {[
              ["Product variants", report.summary.lines, true],
              ["Quantity", report.summary.quantity, true],
              ["Purchase total", report.summary.total],
              ["GST", report.summary.tax],
              ["Freight", report.summary.freight],
            ].map(([l, v, qty]) => (
              <div
                key={l}
                className="rounded-2xl border border-gray-100 bg-white p-5 shadow-sm"
              >
                <p className="text-sm text-gray-500">{l}</p>
                <p className="mt-2 text-2xl font-bold text-primary-800">
                  {qty ? Number(v).toLocaleString("en-IN") : money(v)}
                </p>
              </div>
            ))}
          </section>
          <section className="overflow-x-auto rounded-3xl border border-gray-100 bg-white shadow-sm">
                  {sortControls}
            <table className="min-w-[1650px] w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  {[
                    "GRN",
                    "Invoice",
                    "Date",
                    "Supplier",
                    "Store",
                    "Product / Article / Variant",
                    "SKU / Colour / Size",
                    "HSN",
                    "Qty",
                    "Cost",
                    "Purchase value",
                    "CGST",
                    "SGST",
                    "IGST",
                    "Freight",
                    "Landed cost",
                    "Total",
                  ].map((h) => (
                    <th key={h} className="px-4 py-3">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((r) => (
                  <tr key={r.variantId} className="border-t">
                    <td className="px-4 py-3 font-semibold">{r.grnNumber}</td>
                    <td className="px-4 py-3">{r.supplierInvoiceNumber}</td>
                    <td className="px-4 py-3">{date(new Date(r.date))}</td>
                    <td className="px-4 py-3">{r.supplier}</td>
                    <td className="px-4 py-3">{r.store}</td>
                    <td className="px-4 py-3">
                      {r.product}
                      <p className="text-xs text-gray-500">
                        {r.articleNo || "—"} · {r.variant || "Default"}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      {r.sku}
                      <p className="text-xs text-gray-500">
                        {r.color || "—"} / {r.size || "—"}
                      </p>
                    </td>
                    <td className="px-4 py-3">{r.hsnCode || "—"}</td>
                    <td className="px-4 py-3 text-right">{r.quantity}</td>
                    <td className="px-4 py-3 text-right">
                      {money(r.unitCost)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {money(r.purchaseValue)}
                    </td>
                    <td className="px-4 py-3 text-right">{money(r.cgst)}</td>
                    <td className="px-4 py-3 text-right">{money(r.sgst)}</td>
                    <td className="px-4 py-3 text-right">{money(r.igst)}</td>
                    <td className="px-4 py-3 text-right">{money(r.freight)}</td>
                    <td className="px-4 py-3 text-right">
                      {money(r.landedUnitCost)}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">
                      {money(r.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
          <section className="rounded-3xl border border-gray-100 bg-white p-5 shadow-sm">
            <button
              onClick={exportExcel}
              disabled={!canExport || exporting}
              className="inline-flex items-center gap-2 rounded-xl border border-primary-200 px-4 py-2.5 font-semibold text-primary-800 disabled:opacity-50"
            >
              {exporting ? (
                <FiLoader className="animate-spin" />
              ) : (
                <FiDownload />
              )}
              {exporting ? "Preparing…" : "Export Excel"}
            </button>
          </section>
        </>
      )}
      {!report && (
        <section className="rounded-3xl border border-dashed border-gray-300 bg-white p-16 text-center">
          <FiFileText className="mx-auto mb-4 text-4xl text-primary-300" />
          <h2 className="text-xl font-bold">No preview generated yet</h2>
        </section>
      )}
      <ReportFilterModal
        open={showFilters}
        title="Purchase report filters"
        description="Choose a reporting period, then generate a fresh preview."
        loading={loading}
        onApply={applyFilters}
        onClose={() => setShowFilters(false)}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-sm font-semibold text-slate-700">
            From date
            <input
              type="date"
              value={draftFilters.from}
              onChange={(e) =>
                setDraftFilters((v) => ({ ...v, from: e.target.value }))
              }
              className="report-input mt-1.5"
            />
          </label>
          <label className="text-sm font-semibold text-slate-700">
            To date
            <input
              type="date"
              value={draftFilters.to}
              onChange={(e) =>
                setDraftFilters((v) => ({ ...v, to: e.target.value }))
              }
              className="report-input mt-1.5"
            />
          </label>
        </div>
      </ReportFilterModal>
    </div>
  );
}
